import { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import { Batch } from '../../models/Batch';
import { Customer, SalesInvoice } from '../../models/Sales';
import Tenant from '../../models/Tenant';
import { toDate } from '../../utils/timestamp';

const space = {
  text: ' ',
  margin: [0, 5, 0, 0], // [left, top]
};

const auto = {
  width: '*',
  text: '',
};

// Header section only (FROM + INVOICE layout)
export const SalesInvoiceDefinitionHeader = (invoice: SalesInvoice, logoBase64?: string): Content => ({
  table: {
    widths: ['*'],
    body: [
      [
        {
          margin: [0, 0, 0, 0],
          columns: [
            {
              width: 'auto',
              alignment: 'left',
              stack: [
                {
                  text: 'INVOICE',
                  style: 'header',
                },
                space,
                {
                  text: `Invoice Number:`,
                },
                {
                  text: invoice.invoiceNumber,
                },
                space,
                {
                  text: `Date: ${toDate(invoice.invoiceDate).toLocaleDateString()}`,
                },
              ],
            },
            auto,
            ...(logoBase64
              ? [
                  {
                    width: 'auto',
                    image: logoBase64,
                    fit: [100, 100],
                    alignment: 'right',
                  },
                ]
              : []),
          ],
          columnGap: 20,
        },
      ],
    ],
  },
  layout: {
    fillColor: () => '#e7b562', // light gray background
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    paddingLeft: () => 50,
    paddingRight: () => 50,
    paddingTop: () => 20,
    paddingBottom: () => 20,
  },
});

export const SalesInvoiceDefinitionToFrom = (tenant: Tenant, customer: Customer): Content => ({
  columns: [
    {
      stack: [
        { text: 'TO:', bold: true, margin: [0, 0, 0, 2] },
        { text: customer.name ?? '—' },
        { text: customer.address ?? '' },
        {
          text: `${customer.zipCode ?? ''} ${customer.city ?? ''}`.trim(),
        },
      ],
    },
    {
      stack: [
        { text: 'FROM:', bold: true, margin: [0, 0, 0, 2] },
        { text: tenant.companyName ?? '' },
        { text: tenant.companyAddress ?? '' },
        {
          text: `${tenant.companyZipCode ?? ''} ${tenant.companyCity ?? ''}`,
        },
      ],
    },
  ],
  columnGap: 40,
  margin: [50, 20, 50, 20],
});

export const SalesInvoiceDefinitionItemsHeader = (): Content => ({
  table: {
    widths: ['*'],
    body: [
      [
        {
          margin: [0, 0, 0, 0],
          columns: [
            {
              width: '50%',
              alignment: 'left',
              text: 'PRODUCT DESCRIPTION',
              bold: true,
              marginTop: 5,
            },
            {
              width: '6%',
              alignment: 'left',
              text: 'QTY',
              bold: true,
              marginTop: 5,
            },
            {
              width: '10%',
              alignment: 'left',
              text: 'VAT',
              bold: true,
              marginTop: 5,
            },
            {
              width: '13%',
              alignment: 'center',
              text: 'PRICE\nINC VAT',
              bold: true,
            },
            {
              width: '12%',
              alignment: 'left',
              text: 'TOTAL',
              bold: true,
              marginTop: 5,
            },
          ],
          columnGap: 20,
        },
      ],
    ],
  },
  layout: {
    fillColor: () => '#e7b562', // light gray background
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    paddingLeft: () => 50,
    paddingRight: () => 20,
    paddingTop: () => 5,
    paddingBottom: () => 5,
  },
});

export const SalesInvoiceDefinitionItems = (
  invoice: SalesInvoice,
  batchMap: Record<string, Batch>,
  currencySymbol = '€',
  currencyPosition: 'before' | 'after' = 'before',
): Content => ({
  table: {
    widths: ['*'],
    body: [
      // One row per invoice item
      ...[...(invoice.items ?? []), { batchId: undefined, quantity: undefined, unitPrice: undefined }].map((item) => [
        {
          columns: [
            {
              width: '50%',
              alignment: 'left',
              text: item?.batchId ? `${batchMap[item.batchId].recipe?.name} - ${item.productName}` : '',
            },
            {
              width: '7%',
              alignment: 'center',
              text: item?.quantity?.toString() ?? 'Total',
            },
            {
              width: '9%',
              alignment: 'center',
              text: item?.unitPrice ? '0%' : '', // adjust if VAT per item is available
            },
            {
              width: '13%',
              alignment: 'center',
              text: item?.unitPrice
                ? currencyPosition === 'before'
                  ? `${currencySymbol}${item.unitPrice.toFixed(2)}`
                  : `${item.unitPrice.toFixed(2)}${currencySymbol}`
                : '',
            },
            {
              width: '12%',
              alignment: 'left',
              text: item?.unitPrice
                ? currencyPosition === 'before'
                  ? `${currencySymbol}${(item.quantity * item.unitPrice).toFixed(2)}`
                  : `${(item.quantity * item.unitPrice).toFixed(2)}${currencySymbol}`
                : `${currencySymbol}${invoice.total.toFixed(2)}`,
            },
          ],
          columnGap: 20,
        },
      ]),
    ],
  },
  layout: {
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    paddingLeft: () => 50,
    paddingRight: () => 20,
    paddingTop: () => 5,
    paddingBottom: () => 5,
  },
});

export const SalesInvoiceDefinitionBankTotal = (invoice: SalesInvoice, tenant: Tenant): Content => ({
  table: {
    widths: ['*'],
    body: [
      [
        {
          margin: [0, 0, 0, 0],
          columns: [
            {
              width: '50%',
              stack: [
                { text: 'BANK DETAILS', bold: true, margin: [0, 0, 0, 2] },
                { text: tenant.bankName ?? '—' },
                { text: tenant.companyName ?? '—' },
                { text: tenant.iban ?? '—' },
              ],
            },
          ],
          columnGap: 20,
        },
      ],
    ],
  },
  layout: {
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    paddingLeft: () => 50,
    paddingRight: () => 20,
    paddingTop: () => 5,
    paddingBottom: () => 5,
  },
});

export const SalesInvoiceDefinitionFooter = (tenant: Tenant, currentPage: number, pageCount: number): Content => ({
  margin: [50, 10, 50, 10],
  columns: [
    {
      stack: [
        { text: [{ text: 'TEL: ', bold: true }, tenant.companyPhone ?? '—'] },
        { text: [{ text: 'EMAIL: ', bold: true }, tenant.email ?? '—'] },
        { text: [{ text: 'KVK: ', bold: true }, tenant.kvkNumber ?? '—'] },
        { text: [{ text: 'VAT: ', bold: true }, tenant.vatNumber ?? '—'] },
      ],
      alignment: 'left',
      fontSize: 8,
    },
    {
      text: `${currentPage} of ${pageCount}`,
      alignment: 'center',
      fontSize: 8,
    },
    {
      stack: [{ text: 'The Horrible Meadery' }, { text: 'Amsterdam' }, { text: 'est 2023' }],
      alignment: 'center',
      italics: true,
      fontSize: 8,
    },
  ],
  columnGap: 20,
});

export const SalesInvoiceDefinitionNotes = (notes: string): Content => ({
  table: {
    widths: ['*', '50%'], // Two columns: left (flex) and right (fixed 50%)
    body: [
      [
        { text: '' },
        {
          fillColor: '#e7b562',
          stack: [
            { text: 'NOTES', bold: true, margin: [0, 0, 0, 4] },
            { text: notes || '—', fontSize: 10 },
          ],
          margin: [10, 10, 10, 0], // Inner padding
        },
      ],
    ],
  },
  layout: {
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    paddingLeft: () => 0,
    paddingRight: () => 0,
    paddingTop: () => 0,
    paddingBottom: () => 10,
  },
  margin: [0, 20, 0, 0], // No right margin
});

// Master invoice definition (composed of parts)
export const SalesInvoiceDefinition = (
  tenantObject: Tenant,
  invoice: SalesInvoice,
  batches: Record<string, Batch>,
  customer: Customer,
  logoBase64?: string,
  currencySymbol = '€',
  currencyPosition: 'before' | 'after' = 'before',
): TDocumentDefinitions => {
  return {
    footer: (currentPage, pageCount) => SalesInvoiceDefinitionFooter(tenantObject, currentPage, pageCount),

    content: [
      SalesInvoiceDefinitionHeader(invoice, logoBase64),
      SalesInvoiceDefinitionToFrom(tenantObject, customer),
      SalesInvoiceDefinitionItemsHeader(),
      SalesInvoiceDefinitionItems(invoice, batches, currencySymbol, currencyPosition),
      SalesInvoiceDefinitionBankTotal(invoice, tenantObject),
      ...(tenantObject.invoiceNotes ? [SalesInvoiceDefinitionNotes(tenantObject.invoiceNotes)] : []),
    ],
    pageMargins: [0, 0, 0, 52],
    styles: {
      header: { fontSize: 24, bold: true },
      subheader: { fontSize: 12, bold: true },
      footer: { fontSize: 10, italics: true },
    },
  };
};
